"use client";

import React, { useState } from "react";
import { useHomepageContext } from "../context/HomepageContext";

interface EditableTextProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    as?: "h1" | "h2" | "h3" | "p" | "span";
    placeholder?: string;
    style?: React.CSSProperties;
}

export default function EditableText({
    value,
    onChange,
    className = "",
    as = "span",
    placeholder = "Click to edit...",
    style,
}: EditableTextProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);

    // We need to make sure the context is available or fallback gracefully
    const context = useHomepageContext();
    const canEdit = context ? context.canEdit : true;

    const handleBlur = () => {
        setIsEditing(false);
        onChange(editValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleBlur();
        }
        if (e.key === "Escape") {
            setEditValue(value);
            setIsEditing(false);
        }
    };

    if (isEditing && canEdit) {
        return (
            <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                className={`${className} bg-transparent border-b-2 border-blue-400 outline-none w-full`}
                placeholder={placeholder}
                style={style}
            />
        );
    }

    const Tag = as;
    return (
        <Tag
            onClick={() => {
                if (!canEdit) return;
                setEditValue(value);
                setIsEditing(true);
            }}
            className={`${className} ${canEdit ? "cursor-text outline-none rounded transition-all px-1 -mx-1" : ""}`}
            style={style}
        >
            {value ||
                (canEdit && (
                    <span className="text-gray-400 italic">{placeholder}</span>
                ))}
        </Tag>
    );
}
